/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Save } from 'lucide-react';
import { Issue, IssueType, IssueStatus, EstimateSize } from '@shared/utils/issues';

interface IssueFormProps {
  formData: Partial<Issue>;
  issueTypes: IssueType[];
  statusOptions: IssueStatus[];
  estimateOptions: EstimateSize[];
  onInputChange: (field: keyof Issue, value: any) => void;
  onArrayInputChange: (field: 'links' | 'labels', value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export function IssueForm({
  formData,
  issueTypes,
  statusOptions,
  estimateOptions,
  onInputChange,
  onArrayInputChange,
  onSubmit,
  onCancel
}: IssueFormProps) {
  return (
    <div style={{ 
      border: '1px solid #ddd', 
      borderRadius: '8px', 
      padding: '24px', 
      marginBottom: '24px',
      background: '#f9f9f9'
    }}>
      <h3 style={{ marginBottom: '20px' }}>Create New Issue</h3>
      <form onSubmit={onSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div className="form-group">
            <label>Type</label>
            <select
              value={formData.type}
              onChange={(e) => onInputChange('type', e.target.value)}
            >
              {issueTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => onInputChange('status', e.target.value)}
            >
              {statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => onInputChange('title', e.target.value)}
            placeholder="Brief description of the issue"
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => onInputChange('description', e.target.value)}
            placeholder="Detailed description of the issue"
            rows={4}
            required
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div className="form-group">
            <label>Assigned To</label>
            <input
              type="text"
              value={formData.assignedTo}
              onChange={(e) => onInputChange('assignedTo', e.target.value)}
              placeholder="Username or email"
            />
          </div>
          
          <div className="form-group">
            <label>Estimate</label>
            <select
              value={formData.estimate}
              onChange={(e) => onInputChange('estimate', e.target.value)}
            >
              {estimateOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label>Effort (Hours)</label>
            <input
              type="number"
              min="0"
              value={formData.effort}
              onChange={(e) => onInputChange('effort', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div className="form-group">
            <label>Version</label>
            <input
              type="text"
              value={formData.version}
              onChange={(e) => onInputChange('version', e.target.value)}
              placeholder="e.g., v1.2.0"
            />
          </div>
          
          <div className="form-group">
            <label>Plan (tasks/ path)</label>
            <input
              type="text"
              value={formData.plan}
              onChange={(e) => onInputChange('plan', e.target.value)}
              placeholder="e.g., tasks/feature-implementation.md"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Links (comma-separated)</label>
          <input
            type="text"
            value={formData.links?.join(', ') || ''}
            onChange={(e) => onArrayInputChange('links', e.target.value)}
            placeholder="https://example.com, https://docs.example.com"
          />
        </div>

        <div className="form-group">
          <label>Labels (comma-separated)</label>
          <input
            type="text"
            value={formData.labels?.join(', ') || ''}
            onChange={(e) => onArrayInputChange('labels', e.target.value)}
            placeholder="urgent, frontend, api"
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button type="submit" className="btn">
            <Save size={16} style={{ marginRight: '8px' }} />
            Create Issue
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

